/**
 * FR-28 Phase 3 — Platform console.
 *
 * The operator's view of every institution on the platform. Onboarding is
 * deliberately not self-service: institutions are registered here, by the
 * platform owner, which is why this screen also creates the first admin
 * account and shows its temporary password exactly once.
 */
import React from "react"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useT } from "../shared"
import { Btn, Badge, TableWrap } from "../shared/components"
import { listTenants, createTenant, updateTenant, getPlanCatalog } from "../api/tenants.api"
import { teksFitur, dariPilihan, kePilihan } from "../lib/fiturLembaga"
import { tenantAddress } from "../lib/platformDomain"

const EMPTY_FORM = {
  slug: "",
  name: "",
  adminName: "",
  adminEmail: "",
  cert_prefix: "",
  primary_color: "",
}

// Mirrors the server rule: a slug becomes a subdomain, so it must be a valid
// DNS label. Checked here too so the user gets feedback before submitting.
const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/

/**
 * Nilai batas dari kotak isian → angka, atau null bila tidak dibatasi.
 *
 * Kosong, spasi, nol, dan angka tak masuk akal semuanya berarti "ikut bawaan
 * paket". Batas nol tidak pernah punya arti — dan bila tersimpan, ia mengunci
 * lembaga sepenuhnya dari membuat program baru, dengan satu-satunya petunjuk
 * berupa tulisan "3/0" di layar Settings. Server juga menolaknya, tetapi
 * penyaringan di sini yang membuat operator tidak pernah sampai mengirimnya.
 */
function batasAtauKosong(nilai) {
  const n = Number(String(nilai ?? '').trim())
  return String(nilai ?? '').trim() === '' || !Number.isFinite(n) || n < 1 ? null : Math.floor(n)
}

export default function PlatformScreen() {
  const { lang, branding } = useApp()
  // Domain induk datang dari server; lihat lib/platformDomain.js untuk alasannya.
  const domain = branding?.platform_domain
  const t = useT()
  const id = lang === "id"

  const [tenants, setTenants] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [showForm, setShowForm] = React.useState(false)
  const [form, setForm] = React.useState(EMPTY_FORM)
  const [saving, setSaving] = React.useState(false)
  // Shown once after creation — the password is never retrievable again.
  const [handover, setHandover] = React.useState(null)
  // Which institution's plan is being edited, and the draft values.
  const [planEdit, setPlanEdit] = React.useState(null)
  // Paket dan fitur bawaannya, dari server. Bila gagal dimuat, bagian "Fitur"
  // di editor disembunyikan — paket, masa aktif, dan batas tetap bisa disimpan.
  const [catalog, setCatalog] = React.useState(null)

  const reload = React.useCallback(async () => {
    setLoading(true)
    try {
      setTenants(await listTenants())
    } catch {
      toast.error(id ? "Gagal memuat daftar lembaga" : "Failed to load institutions")
    } finally {
      setLoading(false)
    }
  }, [id])

  React.useEffect(() => { reload() }, [reload])
  React.useEffect(() => { getPlanCatalog().then(setCatalog).catch(() => {}) }, [])

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  // Suggest a slug from the institution name so the operator rarely types it.
  function onNameChange(e) {
    const name = e.target.value
    setForm((f) => {
      const autoSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
      // Only overwrite while the slug is still following the name.
      const slugWasAuto = !f.slug || f.slug === f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")
      return { ...f, name, slug: slugWasAuto ? autoSlug : f.slug }
    })
  }

  async function submit(e) {
    e.preventDefault()
    if (!SLUG_RE.test(form.slug)) {
      toast.error(id
        ? "Slug hanya boleh huruf kecil, angka, dan tanda hubung"
        : "Slug may only contain lowercase letters, digits and hyphens")
      return
    }
    setSaving(true)
    try {
      const payload = {
        slug: form.slug,
        name: form.name,
        adminName: form.adminName,
        adminEmail: form.adminEmail,
        ...(form.cert_prefix ? { cert_prefix: form.cert_prefix } : {}),
        ...(form.primary_color ? { primary_color: form.primary_color } : {}),
      }
      const res = await createTenant(payload)
      setHandover(res)
      setForm(EMPTY_FORM)
      setShowForm(false)
      reload()
    } catch (err) {
      toast.error(err?.response?.data?.error || (id ? "Gagal mendaftarkan lembaga" : "Failed to register institution"))
    } finally {
      setSaving(false)
    }
  }

  function openPlanEditor(tenant) {
    setPlanEdit({
      id: tenant.id,
      name: tenant.name,
      plan: tenant.plan || 'BASIC',
      // <input type="date"> needs YYYY-MM-DD; empty means no expiry.
      expiresAt: tenant.plan_expires_at ? tenant.plan_expires_at.slice(0, 10) : '',
      maxParticipants: tenant.max_participants ?? '',
      maxPrograms: tenant.max_programs ?? '',
      // Per fitur: 'paket' (ikut paket) | 'on' | 'off'.
      fitur: Object.fromEntries(
        (catalog?.features || []).map((k) => [k, kePilihan(tenant.featureOverrides?.[k])])
      ),
    })
  }

  async function savePlan(e) {
    e.preventDefault()
    setSaving(true)
    try {
      await updateTenant(planEdit.id, {
        plan: planEdit.plan,
        // Empty date = perpetual, which is what grandfathered and open-ended
        // arrangements use.
        plan_expires_at: planEdit.expiresAt ? planEdit.expiresAt : null,
        // Blank override = fall back to whatever the tier itself allows.
        //
        // Nol dan spasi ikut diperlakukan sebagai kosong. Sebelumnya hanya
        // string kosong yang menjadi null, sehingga operator yang mengetik "0"
        // bermaksud "tanpa batas" justru memasang batas keras nol — dan lembaga
        // itu tidak bisa membuat program sama sekali. `Number(" ")` juga 0,
        // jadi satu spasi pun dulu cukup untuk menguncinya.
        max_participants: batasAtauKosong(planEdit.maxParticipants),
        max_programs: batasAtauKosong(planEdit.maxPrograms),
        // Objek penuh yang menggantikan isi sebelumnya (null = ikut paket).
        // Dikirim hanya bila katalog termuat: tanpanya formulir tidak tahu
        // fitur apa saja yang ada, dan mengirim objek kosong akan menghapus
        // pengaturan yang sudah tersimpan.
        ...(catalog && {
          feature_overrides: Object.fromEntries(
            catalog.features.map((k) => [k, dariPilihan(planEdit.fitur?.[k])])
          ),
        }),
      })
      toast.success(id ? 'Paket diperbarui' : 'Plan updated')
      setPlanEdit(null)
      reload()
    } catch (err) {
      toast.error(err?.response?.data?.error || (id ? 'Gagal menyimpan paket' : 'Failed to save plan'))
    } finally {
      setSaving(false)
    }
  }

  // "12/50", or "12/∞" when the tier has no cap. Nol diperlakukan sebagai
  // "tanpa batas" juga: batas nol tidak pernah punya arti, dan menampilkannya
  // apa adanya ("3/0") adalah satu-satunya petunjuk yang pernah muncul saat
  // sebuah lembaga terkunci tanpa sebab yang terlihat.
  function usageText(used, limit) {
    return `${used ?? 0}/${!limit ? '∞' : limit}`
  }

  async function toggleActive(tenant) {
    const next = !tenant.is_active
    const question = next
      ? (id ? `Aktifkan kembali ${tenant.name}?` : `Reactivate ${tenant.name}?`)
      : (id
          ? `Nonaktifkan ${tenant.name}? Semua penggunanya tidak akan bisa masuk.`
          : `Suspend ${tenant.name}? All of their users will be locked out.`)
    if (!window.confirm(question)) return
    try {
      await updateTenant(tenant.id, { is_active: next })
      toast.success(next
        ? (id ? "Lembaga diaktifkan" : "Institution reactivated")
        : (id ? "Lembaga dinonaktifkan" : "Institution suspended"))
      reload()
    } catch (err) {
      toast.error(err?.response?.data?.error || (id ? "Gagal mengubah status" : "Failed to update"))
    }
  }

  return (
    <div>
      <div className="row between mb-4" style={{ flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0 }}>{id ? "Lembaga" : "Institutions"}</h1>
          <p className="muted small" style={{ margin: "4px 0 0" }}>
            {id
              ? "Semua lembaga yang memakai platform ini."
              : "Every institution using this platform."}
          </p>
        </div>
        <Btn onClick={() => setShowForm((s) => !s)}>
          <Icon name={showForm ? "close" : "plus"} size={14} />
          {showForm
            ? (id ? "Batal" : "Cancel")
            : (id ? "Daftarkan lembaga" : "Register institution")}
        </Btn>
      </div>

      {/* One-time credential handover */}
      {handover && (
        <div className="card mb-4" style={{ borderColor: "var(--success)" }}>
          <div className="row between mb-2">
            <strong>{id ? "Lembaga berhasil didaftarkan" : "Institution registered"}</strong>
            <button className="icon-btn sm" onClick={() => setHandover(null)} aria-label="Close">
              <Icon name="close" size={14} />
            </button>
          </div>
          <p className="small" style={{ marginTop: 0 }}>
            {id
              ? "Serahkan data masuk berikut ke admin lembaga. Sandi ini hanya ditampilkan sekali."
              : "Hand these sign-in details to the institution's admin. This password is shown only once."}
          </p>
          <TableWrap>
            <table className="table">
              <tbody>
                <tr>
                  <td className="muted small">{id ? "Lembaga" : "Institution"}</td>
                  <td className="small">{handover.tenant?.name}</td>
                </tr>
                <tr>
                  <td className="muted small">{id ? "Alamat" : "Address"}</td>
                  <td className="small mono">{tenantAddress(handover.tenant?.slug, domain)}</td>
                </tr>
                <tr>
                  <td className="muted small">Email</td>
                  <td className="small mono">{handover.admin?.email}</td>
                </tr>
                <tr>
                  <td className="muted small">{id ? "Sandi sementara" : "Temporary password"}</td>
                  <td className="small mono" style={{ fontWeight: 600 }}>{handover.temporaryPassword}</td>
                </tr>
              </tbody>
            </table>
          </TableWrap>
        </div>
      )}

      {/* Plan editor */}
      {planEdit && (
        <form className="card mb-4" onSubmit={savePlan}>
          <div className="row between mb-3">
            <strong>{id ? `Paket — ${planEdit.name}` : `Plan — ${planEdit.name}`}</strong>
            <button type="button" className="icon-btn sm" onClick={() => setPlanEdit(null)} aria-label="Close">
              <Icon name="close" size={14} />
            </button>
          </div>
          <div className="grid grid-2" style={{ gap: 12 }}>
            <label className="field">
              <span className="field-label">{id ? "Paket" : "Plan"}</span>
              <select
                className="input"
                value={planEdit.plan}
                onChange={(e) => setPlanEdit((p) => ({ ...p, plan: e.target.value }))}
              >
                <option value="BASIC">Paket 1</option>
                <option value="PROFESSIONAL">Paket 2</option>
                <option value="ENTERPRISE">Enterprise</option>
              </select>
            </label>
            <label className="field">
              <span className="field-label">{id ? "Berlaku sampai" : "Valid until"}</span>
              <input
                className="input"
                type="date"
                value={planEdit.expiresAt}
                onChange={(e) => setPlanEdit((p) => ({ ...p, expiresAt: e.target.value }))}
              />
              <span className="tiny muted">
                {id
                  ? "Kosongkan = tanpa batas waktu. Setelah lewat, lembaga jadi baca-saja."
                  : "Empty = no expiry. Past this date the institution becomes read-only."}
              </span>
            </label>
            <label className="field">
              <span className="field-label">
                {id ? "Batas peserta (opsional)" : "Participant cap (optional)"}
              </span>
              <input
                className="input" type="number" min="1"
                value={planEdit.maxParticipants}
                onChange={(e) => setPlanEdit((p) => ({ ...p, maxParticipants: e.target.value }))}
                placeholder={id ? "ikut bawaan paket" : "use plan default"}
              />
            </label>
            <label className="field">
              <span className="field-label">
                {id ? "Batas program (opsional)" : "Program cap (optional)"}
              </span>
              <input
                className="input" type="number" min="1"
                value={planEdit.maxPrograms}
                onChange={(e) => setPlanEdit((p) => ({ ...p, maxPrograms: e.target.value }))}
                placeholder={id ? "ikut bawaan paket" : "use plan default"}
              />
            </label>
          </div>
          <p className="tiny muted" style={{ marginTop: 10 }}>
            {id
              ? "Batas khusus dipakai untuk kesepakatan Enterprise. Dikosongkan berarti mengikuti batas bawaan paket."
              : "Caps are for negotiated Enterprise deals. Leave blank to follow the plan's own limits."}
          </p>

          {catalog && (
            <fieldset style={{ border: 0, padding: 0, margin: "16px 0 0" }}>
              <legend className="field-label" style={{ padding: 0, marginBottom: 4 }}>
                {id ? "Fitur lembaga ini" : "Features for this institution"}
              </legend>
              <p className="tiny muted" style={{ margin: "0 0 8px" }}>
                {id
                  ? "Bawaannya mengikuti paket. Ubah hanya untuk kesepakatan khusus: nyalakan satu fitur tanpa menaikkan paket, atau matikan yang tidak diperlukan. Pengaturan ini tetap berlaku bila paketnya diganti."
                  : "Follows the plan by default. Change only for special deals: enable one feature without upgrading, or switch one off. These settings persist if the plan changes."}
              </p>
              <div className="stack" style={{ gap: 8 }}>
                {catalog.features.map((k) => {
                  const dariPaket = !!catalog.plans.find((p) => p.key === planEdit.plan)?.features?.[k]
                  const pilihan = planEdit.fitur?.[k] || "paket"
                  const berlaku = pilihan === "on" ? true : pilihan === "off" ? false : dariPaket
                  const { nama, info } = teksFitur(k, lang)
                  return (
                    <div key={k} className="row between" style={{ gap: 12, alignItems: "center" }}>
                      <div style={{ minWidth: 0 }}>
                        <div className="small">
                          {nama}{" "}
                          <Badge tone={berlaku ? "success" : "default"}>
                            {berlaku ? (id ? "aktif" : "on") : (id ? "nonaktif" : "off")}
                          </Badge>
                        </div>
                        {info && <div className="tiny muted">{info}</div>}
                      </div>
                      <select
                        className="input"
                        style={{ width: 210, flexShrink: 0 }}
                        aria-label={nama}
                        value={pilihan}
                        onChange={(e) =>
                          setPlanEdit((p) => ({ ...p, fitur: { ...p.fitur, [k]: e.target.value } }))
                        }
                      >
                        <option value="paket">
                          {id
                            ? `Ikut paket (${dariPaket ? "aktif" : "nonaktif"})`
                            : `Follow plan (${dariPaket ? "on" : "off"})`}
                        </option>
                        <option value="on">{id ? "Paksa aktif" : "Force on"}</option>
                        <option value="off">{id ? "Paksa nonaktif" : "Force off"}</option>
                      </select>
                    </div>
                  )
                })}
              </div>
            </fieldset>
          )}
          <div className="row gap-2 mt-3">
            <Btn type="submit" disabled={saving}>
              {saving ? (id ? "Menyimpan…" : "Saving…") : (id ? "Simpan" : "Save")}
            </Btn>
            <Btn variant="ghost" type="button" onClick={() => setPlanEdit(null)}>
              {id ? "Batal" : "Cancel"}
            </Btn>
          </div>
        </form>
      )}

      {/* Registration form */}
      {showForm && (
        <form className="card mb-4" onSubmit={submit}>
          <div className="grid grid-2" style={{ gap: 12 }}>
            <label className="field">
              <span className="field-label">{id ? "Nama lembaga" : "Institution name"}</span>
              <input className="input" value={form.name} onChange={onNameChange} required
                placeholder={id ? "mis. PT Pabrik Sejahtera" : "e.g. Acme Manufacturing"} />
            </label>
            <label className="field">
              <span className="field-label">{id ? "Alamat (subdomain)" : "Address (subdomain)"}</span>
              <input className="input mono" value={form.slug} onChange={set("slug")} required
                placeholder="pabrik" />
              <span className="tiny muted">{tenantAddress(form.slug || "…", domain)}</span>
            </label>
            <label className="field">
              <span className="field-label">{id ? "Nama admin pertama" : "First admin's name"}</span>
              <input className="input" value={form.adminName} onChange={set("adminName")} required />
            </label>
            <label className="field">
              <span className="field-label">{id ? "Email admin" : "Admin email"}</span>
              <input className="input" type="email" value={form.adminEmail} onChange={set("adminEmail")} required />
            </label>
            <label className="field">
              <span className="field-label">
                {id ? "Awalan sertifikat (opsional)" : "Certificate prefix (optional)"}
              </span>
              <input className="input mono" value={form.cert_prefix} onChange={set("cert_prefix")}
                placeholder={form.slug ? form.slug.toUpperCase() : "PABRIK"} />
            </label>
            <label className="field">
              <span className="field-label">{id ? "Warna utama (opsional)" : "Primary colour (optional)"}</span>
              <input className="input mono" value={form.primary_color} onChange={set("primary_color")}
                placeholder="#16A34A" />
            </label>
          </div>
          <div className="row gap-2 mt-3">
            <Btn type="submit" disabled={saving}>
              {saving ? (id ? "Menyimpan…" : "Saving…") : (id ? "Daftarkan" : "Register")}
            </Btn>
            <Btn variant="ghost" type="button" onClick={() => { setShowForm(false); setForm(EMPTY_FORM) }}>
              {id ? "Batal" : "Cancel"}
            </Btn>
          </div>
        </form>
      )}

      {/* Institution list */}
      <div className="card">
        {loading ? (
          <p className="muted small" style={{ margin: 0 }}>{id ? "Memuat…" : "Loading…"}</p>
        ) : tenants.length === 0 ? (
          <p className="muted small" style={{ margin: 0 }}>
            {id ? "Belum ada lembaga." : "No institutions yet."}
          </p>
        ) : (
          <TableWrap>
            <table className="table">
              <thead>
                <tr>
                  <th>{id ? "Lembaga" : "Institution"}</th>
                  <th>{id ? "Alamat" : "Address"}</th>
                  <th>{id ? "Paket" : "Plan"}</th>
                  <th style={{ textAlign: "right" }}>{id ? "Peserta" : "Participants"}</th>
                  <th style={{ textAlign: "right" }}>{id ? "Program" : "Programs"}</th>
                  <th>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((tn) => (
                  <tr key={tn.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{tn.name}</div>
                      {tn.cert_prefix && (
                        <div className="tiny muted mono">{tn.cert_prefix}/…</div>
                      )}
                    </td>
                    <td className="small mono">{tenantAddress(tn.slug, domain)}</td>
                    <td>
                      <div className="small">{tn.planLabel || tn.plan}</div>
                      {Object.keys(tn.featureOverrides || {}).length > 0 && (
                        <div className="tiny muted">
                          {id
                            ? `${Object.keys(tn.featureOverrides).length} fitur khusus`
                            : `${Object.keys(tn.featureOverrides).length} custom feature(s)`}
                        </div>
                      )}
                      <div className="tiny muted">
                        {tn.plan_expires_at
                          ? `${id ? "s.d." : "until"} ${tn.plan_expires_at.slice(0, 10)}`
                          : (id ? "tanpa batas waktu" : "no expiry")}
                      </div>
                    </td>
                    {/* Usage against the cap, so an institution nearing its
                        limit is visible before they hit it and complain. */}
                    <td className="small" style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {usageText(tn.usage?.participants, tn.limits?.participants)}
                    </td>
                    <td className="small" style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                      {usageText(tn.usage?.programs, tn.limits?.programs)}
                    </td>
                    <td>
                      {/* Suspension and expiry are different states and must
                          not be conflated: suspended means locked out entirely,
                          expired means read-only. */}
                      {!tn.is_active ? (
                        <Badge tone="danger">{id ? "Nonaktif" : "Suspended"}</Badge>
                      ) : tn.isExpired ? (
                        <Badge tone="warning">{id ? "Kedaluwarsa" : "Expired"}</Badge>
                      ) : (
                        <Badge tone="success">{id ? "Aktif" : "Active"}</Badge>
                      )}
                    </td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button className="btn-ghost btn-sm" onClick={() => openPlanEditor(tn)}>
                        {id ? "Paket" : "Plan"}
                      </button>
                      <button
                        className="btn-ghost btn-sm"
                        onClick={() => toggleActive(tn)}
                        style={tn.is_active ? { color: "var(--danger)" } : undefined}
                      >
                        {tn.is_active
                          ? (id ? "Nonaktifkan" : "Suspend")
                          : (id ? "Aktifkan" : "Reactivate")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </div>
    </div>
  )
}
